import { AxiosError } from 'axios'
import type { MyOrganisationDTO } from '@api/fastAPI.schemas'
import { useGetMyOrganisation } from '@api/my-organisation/my-organisation'
import { useAppStore } from '@stores'

interface OrganisationAdminState {
  isOrganisationAdmin: boolean
  isLoading: boolean
}

/**
 * Whether the user administers their own organisation, which is what allows
 * changing its shared library (Aufbauten, Anschlüsse, Vorlagen).
 *
 * Not to be confused with `useHasAdminPermission`: that is the Modugen staff
 * flag from the access token and says nothing about a particular company.
 *
 * Assumes "not an admin" while loading and on error, so the UI never offers a
 * control the backend would refuse. Errors are otherwise ignored on purpose --
 * a user with no organisation gets the 403 the project list already handles,
 * and a second snackbar about it here would only be noise.
 *
 * staleTime is bounded rather than Infinity: unlike the Auth0 scope behind
 * `useHasAdminPermission`, this role can be changed by someone else in
 * Benutzerverwaltung while the user has this page open, and the backend
 * re-checks independently either way, so refetching occasionally costs
 * nothing but keeps the UI from being stuck offering (or hiding) a control
 * for the rest of the session.
 */
const ORGANISATION_ADMIN_STALE_TIME_MS = 5 * 60 * 1000

const useIsOrganisationAdmin = (): OrganisationAdminState => {
  const impersonateNormalUser = useAppStore(state => state.impersonateNormalUser)
  const { data, isLoading } = useGetMyOrganisation<MyOrganisationDTO, AxiosError>({
    query: {
      staleTime: ORGANISATION_ADMIN_STALE_TIME_MS,
      retry: false,
    },
  })

  return {
    isOrganisationAdmin: !impersonateNormalUser && (data?.is_admin ?? false),
    isLoading,
  }
}

export default useIsOrganisationAdmin
